import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EProjectActivityType, EProjectStatus, IProject } from '../data_models/project';
import { IFreelancer } from '../data_models/freelancer';
import { IEmployer } from '../data_models/employer';
import useCurrentUser from '../hooks/useCurrentUser';
import { userIsEmployer, userIsParticipatingFreelancer, userIsProjectManager, userIsRoadmapVerificationProjectManager } from '../utils/projectUtils';

import { ETaskStatus, ITask } from '../data_models/task';

import { FilledButton, OutlinedButton } from '../components/common/Button';
import useRouting, { EProjectDetailsNav } from '../hooks/useRouting';


import { FiUsers } from 'react-icons/fi';

import useGlobalLoading from '../hooks/useGlobalLoading';

import { EUserType } from '../data_models/enums';
import useProjects from '../hooks/useProjects';
import ECookies from '../config/cookies';
import { useCookies } from 'react-cookie';
import useTasks from '../hooks/useTasks';
import useUser from '../hooks/useUser';
import useArielle from '../hooks/useArielle';
import { GoProject, GoTasklist } from 'react-icons/go';
import { IoConstructOutline, IoShieldHalfOutline } from 'react-icons/io5';
import RequestRoadmapVerificationLightbox from '../components/lightboxes/projects/RequestRoadmapVerificationLightbox';
import { ProjectDetailsHeader } from '../components/project/details/ProjectDetailsHeader';
import { ProjectDetailsNavigation } from '../components/project/details/ProjectDetailsNavigation';
import { ProjectDetailsMetrics } from '../components/project/details/ProjectDetailsMetrics';
import { ProjectDetailsOverview } from '../components/project/details/ProjectDetailsOverview';
import { ProjectDetailsRoadmap } from '../components/project/details/ProjectDetailsRoadmap';
import { ProjectDetailsTeam } from '../components/project/details/ProjectDetailsTeam';
import { ProjectDetailsAttachments } from '../components/project/details/ProjectDetailsAttachments';
import { ProjectDetailsChat } from '../components/project/details/ProjectDetailsChat';
import { Invitation } from '../data_models/invitation';
import { Application } from '../data_models/application';
import useApplications from '../hooks/useApplications';
import useInvitations from '../hooks/useInvitations';
import StartProjectSelfManagedLightbox from '../components/lightboxes/projects/StartProjectSelfManagedLightbox';
import InfoBox from '../components/common/InfoBox';
import StartProjectWithPmLightbox from '../components/lightboxes/projects/StartProjectWithPmLightbox';
import HirePmForVerifiedProject from '../components/lightboxes/projects/HirePmForVerifiedProject';

const ProjectDetailsPage: React.FC = () => {

  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('id') as string;
  const navParam = searchParams.get('nav');

  const { currentUser, currentUserType, isLoggedIn } = useCurrentUser();
  const [cookies] = useCookies([
    ECookies.POTENTIAL_PROJECT_DETAIL_DEFINITION_WEAKNESSES_COOKIE,
  ]);


  const { fetchProjectById } = useProjects();
  const { fetchTasksByProjectId } = useTasks();
  const { fetchFreelancerById, fetchEmployerById, fetchBriefFreelancersByIds } = useUser();

  const { goToDashboard, goToLogin, goToSignup, goToProjectDetails, goToBrowseFreelancers } = useRouting();
  const { setLoading, loading } = useGlobalLoading();
  const { lastReceivedProjectRoadmap, lastReceivedProjectDetails } = useArielle();
  const { getNumberOfApplicationsForProject, fetchApplicationByProjectId } = useApplications();
  const { getNumberOfInvitationsForProject, fetchInvitationsForProject } = useInvitations();


  const [project, setProject] = useState<IProject>();
  const [amountOfApplications, setAmountOfApplications] = useState<number>(0);
  const [amountOfInvitations, setAmountOfInvitations] = useState<number>(0);
  const [applications, setApplications] = useState<Application[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [applicants, setApplicants] = useState<IFreelancer[]>([]);
  const [invitedFreelancers, setInvitedFreelancers] = useState<IFreelancer[]>([]);


  const [isPm, setIsPm] = useState(false);
  const [isRoadmapVerificationPm, setIsRoadmapVerificationPm] = useState(false);
  const [isEmployer, setIsEmployer] = useState(false);
  const [isFreelancer, setIsFreelancer] = useState(false);
  const [isOutsideFreelancer, setIsOutsideFreelancer] = useState(false);
  const [isOutsideEmployer, setIsOutsideEmployer] = useState(false);

  const [projectManager, setProjectManager] = useState<IFreelancer>();
  const [roadmapVerificationPm, setRoadmapVerificationPm] = useState<IFreelancer>();
  const [employer, setEmployer] = useState<IEmployer>();
  const [team, setTeam] = useState<IFreelancer[]>([]);
  const [tasks, setTasks] = useState<ITask[]>([]);
  const [detailWeaknesses, setDetailWeaknesses] = useState<string[] | undefined>(undefined);

  const [initialDataFetched, setInitialDataFetched] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [canRequestRoadmapVerification, setCanRequestRoadmapVerification] = useState(false);
  const [canStartProjectSelfManaged, setCanStartProjectSelfManaged] = useState(false);
  const [canStartProjectWithPm, setCanStartProjectWithPm] = useState(false);
  const [canHirePmForVerifiedProject, setCanHirePmForVerifiedProject] = useState(false);

  const [allTasksCompleted, setAllTasksCompleted] = useState(false);

  const [showRequestRoadmapVerificationLightbox, setShowRequestRoadmapVerificationLightbox] = useState(false);
  const [showEditProjectDetailsContainer, setShowEditProjectDetailsContainer] = useState(false);
  const [showCreateRoadmapContainer, setShowCreateRoadmapContainer] = useState(false);
  const [showStartProjectSelfManagedLightbox, setShowStartProjectSelfManagedLightbox] = useState(false);
  const [showStartProjectWithPmLightbox, setShowStartProjectWithPmLightbox] = useState(false);
  const [showHirePmForVerifiedProjectLightbox, setShowHirePmForVerifiedProjectLightbox] = useState(false);

  const toggleEditProjectDetailsContainer = () => {
    if (!showEditProjectDetailsContainer) {
      setShowCreateRoadmapContainer(false);
    }
    setShowEditProjectDetailsContainer(!showEditProjectDetailsContainer);
  }

  const toggleCreateRoadmapContainer = () => {
    if (!showCreateRoadmapContainer) {
      setShowEditProjectDetailsContainer(false);
    }
    setShowCreateRoadmapContainer(!showCreateRoadmapContainer);
  }

  const onUpdateTask = (updatedTask: ITask) => {
    const newTasks = tasks.map(t => t._id === updatedTask._id ? updatedTask : t);
    setTasks(newTasks);
  }

  const onReplaceTasks = (newTasks: ITask[]) => {
    const newTasksCopy = [...newTasks];
    setTasks(newTasksCopy);
  }

  const onAddTask = (newTask: ITask) => {
    const newTasks = [...tasks, newTask];
    setTasks(newTasks);
  }

  const onDeleteTask = (taskId: string) => {
    const newTasks = tasks.filter(t => t._id !== taskId);
    setTasks(newTasks);
  }

  const onUpdateApplication = (application: Application) => {
    const newApplications = applications.map(a => a._id === application._id ? application : a);
    setApplications(newApplications);
  }

  const onUpdateInvitation = (invitation: Invitation) => {
    const newInvitations = invitations.map(i => i._id === invitation._id ? invitation : i);
    setInvitations(newInvitations);
  }


  // TODO
  const fetchApplicationsAndInvitations = async () => {
    const applications = await fetchApplicationByProjectId(projectId, 1, 99999);
    const invitations = await fetchInvitationsForProject(projectId, 1, 99999);


    // discard all applications/invitations that are not for any of the existing tasks. Also filter "double" applications/invitations. Those are applications/invitations that have the same freelancerId and taskId
    const newApplications = applications.filter((application: Application) => tasks.some(t => t._id === application.taskId)).filter((application: Application, index: number, self: Application[]) =>
      index === self.findIndex((t) => t.freelancerId === application.freelancerId && t.taskId === application.taskId)
    );
    const newInvitations = invitations.filter((invitation: Invitation) => tasks.some(t => t._id === invitation.taskId)).filter((invitation: Invitation, index: number, self: Invitation[]) =>
      index === self.findIndex((t) => t.freelancerId === invitation.freelancerId && t.taskId === invitation.taskId)
    );

    // fetch applicants and invited freelancers
    const uniqueApplicantIds = new Set<string>();
    const uniqueInvitedFreelancerIds = new Set<string>();
    for (let i = 0; i < newApplications.length; i++) {
      if (!newApplications[i].freelancerId) continue;
      uniqueApplicantIds.add(newApplications[i].freelancerId);
    }
    for (let i = 0; i < newInvitations.length; i++) {
      if (!newInvitations[i].freelancerId) continue;
      uniqueInvitedFreelancerIds.add(newInvitations[i].freelancerId);
    }

    const applicantsAndInvitedFreelancers = await fetchBriefFreelancersByIds(Array.from(uniqueApplicantIds));
    const invitedFreelancers = await fetchBriefFreelancersByIds(Array.from(uniqueInvitedFreelancerIds));

    setApplications(newApplications);
    setInvitations(newInvitations);

    // setInvitations(newInvitations);
    setApplicants(applicantsAndInvitedFreelancers);
    setInvitedFreelancers(invitedFreelancers);

    setAmountOfInvitations(newInvitations.length);
    setAmountOfApplications(newApplications.length);
  }

  // fetch initial data
  useEffect(() => {
    if (!projectId) return;
    if (!isLoggedIn) return;

    if (!initialDataFetched || refreshing) {
      const fetchProjectAndTasks = async () => {
        setLoading(true);
        try {
          const project = await fetchProjectById(projectId);
          setProject(project);
          const tasks = await fetchTasksByProjectId(projectId);
          setTasks(tasks);
        } catch (error) {
        } finally {
          setLoading(false);
          setInitialDataFetched(true);
          setRefreshing(false);
        }
      }

      fetchProjectAndTasks();
    }
  }, [projectId, isLoggedIn, refreshing]);

  // update project and tasks when new roadmap or details are received from arielle
  useEffect(() => {
    if (lastReceivedProjectRoadmap) {
      if (lastReceivedProjectRoadmap.projectId === projectId) {
        setProject(lastReceivedProjectRoadmap.updatedProject);
        setTasks(lastReceivedProjectRoadmap.roadmapSuggestions[0]);
      }
    }
  }, [lastReceivedProjectRoadmap])

  // update project when new details are received from arielle
  useEffect(() => {
    if (lastReceivedProjectDetails) {
      if (lastReceivedProjectDetails.projectId === projectId) {
        setProject(lastReceivedProjectDetails.updatedProject);
      }
    }
  }, [lastReceivedProjectDetails])


  useEffect(() => {
    if (!project) {
      return;
    }
    setLoading(false);

    const fetchPm = async () => {
      let pm: IFreelancer | undefined;
      if (project.currentProjectManager) {
        try {
          pm = await fetchFreelancerById(project.currentProjectManager);
          setProjectManager(pm);
          if (project.currentRoadmapVerificationPm && project.currentProjectManager !== project.currentRoadmapVerificationPm) {
            const roadmapVerificationPm = await fetchFreelancerById(project.currentRoadmapVerificationPm!);
            setRoadmapVerificationPm(roadmapVerificationPm);
          } else if (project.currentRoadmapVerificationPm) {
            setRoadmapVerificationPm(pm);
          }
        } catch (error) {
        }
      } else if (project.currentRoadmapVerificationPm) {
        // no pm, but there is a roadmap verification pm
        const roadmapVerificationPm = await fetchFreelancerById(project.currentRoadmapVerificationPm!);
        setRoadmapVerificationPm(roadmapVerificationPm);
      }
    }



    const fetchEmployer = async () => {
      try {
        const employer = await fetchEmployerById(project.employerId!);
        setEmployer(employer);
      } catch (error) {
      }
    }

    const fetchTeam = async () => {
      try {
        const team = await fetchBriefFreelancersByIds(project.assignedFreelancers?.map((id) => id.freelancerId) || []);
        const projectManager = project.currentProjectManager ? await fetchFreelancerById(project.currentProjectManager) : undefined;
        const roadmapVerificationPm = project.currentRoadmapVerificationPm ? await fetchFreelancerById(project.currentRoadmapVerificationPm!) : undefined;
        const newTeam = [];
        if (projectManager) {
          newTeam.push(projectManager);
        }
        if (roadmapVerificationPm && project.currentRoadmapVerificationPm !== project.currentProjectManager) {
          newTeam.push(roadmapVerificationPm);
        }
        newTeam.push(...team);
        setTeam(newTeam);
      } catch (error) {
      }
    }

    const fetchAmountsApplicationsAndInvitations = async () => {
      const applications = await getNumberOfApplicationsForProject(projectId);
      const invitations = await getNumberOfInvitationsForProject(projectId);
      setAmountOfApplications(applications);
      setAmountOfInvitations(invitations);
    }

    fetchPm();
    fetchEmployer();
    fetchTeam();
    fetchAmountsApplicationsAndInvitations();

    // set role
    const isPm = userIsProjectManager(project, currentUser!._id!, currentUserType!);
    const isRoadmapVerificationPm = userIsRoadmapVerificationProjectManager(project, currentUser!._id!, currentUserType!);
    const isEmployer = userIsEmployer(project, currentUser!._id!, currentUserType!);
    const isFreelancer = userIsParticipatingFreelancer(project, currentUser!._id!, currentUserType!);
    const isOutsideFreelancer = !isPm && !isEmployer && !isFreelancer && isLoggedIn && currentUserType === EUserType.FREELANCER;
    const isOutsideEmployer = !isPm && !isEmployer && !isFreelancer && isLoggedIn && currentUserType === EUserType.EMPLOYER;

    setIsPm(isPm);
    setIsRoadmapVerificationPm(isRoadmapVerificationPm);
    setIsEmployer(isEmployer);
    setIsFreelancer(isFreelancer);
    setIsOutsideFreelancer(isOutsideFreelancer);
    setIsOutsideEmployer(isOutsideEmployer);

    // Initialize navigation based on role
    let initialNavigation;
    if (isPm) {
      initialNavigation = pmNavigation;
    } else if (isEmployer) {
      initialNavigation = employerNavigation;
    } else if (isFreelancer) {
      initialNavigation = freelancerNavigation;
    } else if (isOutsideFreelancer) {
      initialNavigation = outsideFreelancerNavigation;
    }

    // If we have a nav parameter in the URL, update the navigation accordingly
    const navParam = searchParams.get('nav')?.toLowerCase();
    if (navParam && initialNavigation) {
      const updatedNavigation = initialNavigation.map(item => ({
        ...item,
        current: item.nav.toLowerCase() === navParam
      }));
      setNavigation(updatedNavigation);
    } else if (initialNavigation) {
      setNavigation(initialNavigation);
    }
  }, [project, searchParams]);

  useEffect(() => {
    if (!project?._id) return;
    try {
      // cookie is a map of project id and weaknesses
      const weaknesses = cookies[ECookies.POTENTIAL_PROJECT_DETAIL_DEFINITION_WEAKNESSES_COOKIE];

      // Check if weaknesses is already an object
      const parsedWeaknesses = typeof weaknesses === 'string' ? JSON.parse(weaknesses) : weaknesses;
      const weaknessesArray = parsedWeaknesses?.[project?._id!]?.weaknesses;
      if (weaknessesArray) {
        setDetailWeaknesses(weaknessesArray);
      }
    } catch (error) {
    }
  }, [cookies[ECookies.POTENTIAL_PROJECT_DETAIL_DEFINITION_WEAKNESSES_COOKIE], project]);



  useEffect(() => {
    if (!tasks) return;
    let allCompleted = true;

    for (const task of tasks) {
      if (task.currentStatus !== ETaskStatus.COMPLETED) {
        allCompleted = false;
        break;
      }
    }

    setAllTasksCompleted(allCompleted);
  }, [tasks])


  useEffect(() => {
    if (!project) return;
    if (!isPm && !isEmployer) return;
    // if no tasks, no need to fetch applications and invitations
    if (!tasks || tasks.length === 0) return;

    fetchApplicationsAndInvitations();

  }, [project, isPm, isEmployer, tasks])




  useEffect(() => {
    // can request roadmap verification
    if (isEmployer && project && project?.status !== undefined
      && !project._waitingForRoadmapVerification
      && !project._roadmapVerifiedAt
      && project?.status < EProjectStatus.COMPLETED
      && project.status !== EProjectStatus.IN_PROGRESS_REASSIGN_PM
      && project.status !== EProjectStatus.WAITING_FOR_PM
      && !project.currentRoadmapVerificationPm
      && !project.currentProjectManager
      && tasks.length > 0 
    ) {
      setCanRequestRoadmapVerification(true);
    } else {
      setCanRequestRoadmapVerification(false);
    }


    // Can start project self managed
    if (project && isEmployer && project.status === EProjectStatus.DRAFT &&
      tasks.length > 0 && !project._waitingForRoadmapVerification) {
      setCanStartProjectSelfManaged(true);
    } else {
      setCanStartProjectSelfManaged(false);
    }

    // Can start project with pm
    if (project && isEmployer &&
      ((project.status === EProjectStatus.DRAFT && tasks.length > 0 && project._roadmapVerifiedAt))) {
      setCanStartProjectWithPm(true);
    } else {
      setCanStartProjectWithPm(false);
    }

    // Can hire pm for verified project
    if (project && isEmployer && project.status === EProjectStatus.IN_PROGRESS &&
      project.ownerManaged === true && project._roadmapVerifiedAt) {
      setCanHirePmForVerifiedProject(true);
    } else {
      setCanHirePmForVerifiedProject(false);
    }
  }, [project, tasks, isPm, isRoadmapVerificationPm, isEmployer, isFreelancer, isOutsideFreelancer, isOutsideEmployer])

  const [pmNavigation, setPmNavigation] = useState([
    { pos: 0, name: 'Project Overview', icon: <GoProject />, nav: EProjectDetailsNav.OVERVIEW, current: true },
    { pos: 1, name: 'Roadmap', icon: <GoTasklist />, count: tasks.length, nav: EProjectDetailsNav.ROADMAP, current: false },
    { pos: 2, name: 'Team', icon: <FiUsers />, nav: EProjectDetailsNav.TEAM, current: false },
    // { pos: 3, name: 'Attachments', icon: <RiAttachment2 />, nav: EProjectDetailsNav.ATTACHMENTS, current: false },
    // { pos: 4, name: 'Chat', icon: <BiMessageRounded />, nav: EProjectDetailsNav.CHAT, current: false },
  ]);

  const [employerNavigation, setEmployerNavigation] = useState([
    { pos: 0, name: 'Overview', icon: <GoProject />, nav: EProjectDetailsNav.OVERVIEW, current: true },
    { pos: 1, name: 'Roadmap', icon: <GoTasklist />, count: tasks.length, nav: EProjectDetailsNav.ROADMAP, current: false },
    { pos: 2, name: 'Team', icon: <FiUsers />, nav: EProjectDetailsNav.TEAM, current: false },
    // { pos: 3, name: 'Attachments', icon: <RiAttachment2 />, nav: EProjectDetailsNav.ATTACHMENTS, current: false },
    // { pos: 4, name: 'Chat', icon: <BiMessageRounded />, nav: EProjectDetailsNav.CHAT, current: false },
  ]);

  const [freelancerNavigation, setFreelancerNavigation] = useState([
    { pos: 0, name: 'Overview', icon: <GoProject />, nav: EProjectDetailsNav.OVERVIEW, current: true },
    { pos: 1, name: 'Roadmap', icon: <GoTasklist />, nav: EProjectDetailsNav.ROADMAP, current: false },
    { pos: 2, name: 'Team', icon: <FiUsers />, nav: EProjectDetailsNav.TEAM, current: false },
    // { pos: 3, name: 'Attachments', icon: <RiAttachment2 />, nav: EProjectDetailsNav.ATTACHMENTS, current: false },
    // { pos: 4, name: 'Chat', icon: <BiMessageRounded />, nav: EProjectDetailsNav.CHAT, current: false },
  ]);

  const [outsideFreelancerNavigation, setOutsideFreelancerNavigation] = useState([
    { pos: 0, name: 'Overview', icon: <GoProject />, nav: EProjectDetailsNav.OVERVIEW, current: true },
    { pos: 1, name: 'Roadmap', icon: <GoTasklist />, nav: EProjectDetailsNav.ROADMAP, current: false },

  ]);

  const [navigation, setNavigation] = useState<any[]>([]);

  useEffect(() => {
    const navigation = [];
    if (isPm || isRoadmapVerificationPm) {
      navigation.push(...pmNavigation);

    } else if (isEmployer) {
      navigation.push(...employerNavigation);
    } else if (isFreelancer) {
      navigation.push(...freelancerNavigation);
    } else if (isOutsideFreelancer) {
      navigation.push(...outsideFreelancerNavigation);
    }
    setNavigation(navigation);
  }, [amountOfApplications, amountOfInvitations]);



  return (

    <div className='space-y-4 max-w-7xl mx-auto'>
      {!isLoggedIn
        ? <div className="flex flex-col items-center mt-20 space-y-10">
          <h3>Please login or signup to create a new project</h3>
          <div className="flex flex-row gap-4">
            <OutlinedButton onClick={goToLogin}>Login</OutlinedButton>
            <FilledButton onClick={goToSignup}>Signup for Free</FilledButton>
          </div>
        </div>
        : !loading && !isEmployer && !isFreelancer && !isPm && !isOutsideFreelancer && !isOutsideEmployer
          ? <div className="flex flex-col items-center mt-20 space-y-10">
            <h3 className="font-bold mt-10 text-center">You are not authorized to view this project</h3>
            <FilledButton onClick={() => goToDashboard()}>Go to Dashboard</FilledButton>
          </div>
          : (loading || !project)
            ? <div className='flex flex-row justify-center items-center mt-10 w-full'>
              <p>Loading...</p>
            </div>
            : <div className='p-4'>
              <div className='w-full mt-8'>
                <ProjectDetailsHeader
                  project={project}
                  tasks={tasks}
                  roadmapVerificationPm={roadmapVerificationPm || null}
                  projectManager={projectManager || null}
                  team={team}
                  employer={employer || null}
                  isPm={isPm}
                  isRoadmapVerificationPm={isRoadmapVerificationPm}
                  isEmployer={isEmployer}
                  isFreelancer={isFreelancer}
                  isOutsideFreelancer={isOutsideFreelancer}
                  isOutsideEmployer={isOutsideEmployer}

                  navigation={navigation}
                  setNavigation={setNavigation}

                  setProject={setProject}
                  setTasks={setTasks}
                  refreshing={refreshing}
                  setRefreshing={setRefreshing}
                />
              </div>

              {/* Start Project / Hire PM */}
              {(canStartProjectSelfManaged ||
                canRequestRoadmapVerification ||
                canStartProjectWithPm ||
                canHirePmForVerifiedProject) &&
                <div className='mb-6 w-full p-8 bg-primary-50 border-2 border-primary border-dashed rounded-lg'>
                  <div className='flex flex-col items-center text-center  md:flex-row md:items-start  md:text-left gap-4'>
                    <div className="w-fit h-fit flex items-start justify-center">
                      <IoShieldHalfOutline className="w-28 h-28 text-primary" />
                    </div>
                    <div>
                      <h5 className='font-bold'>{project.status === EProjectStatus.DRAFT
                        ? (canRequestRoadmapVerification
                          ? 'Start With Project Manager?'
                          : 'Start With PM Support?')
                        : project.status === EProjectStatus.IN_PROGRESS && canHirePmForVerifiedProject
                          ? 'Feeling Stuck? Hire a PM!'
                          : ''}
                      </h5>
                      <p className='mt-2'>{project.status === EProjectStatus.DRAFT
                        ? 'Take it easy! Your project manager will kick things off by verifying the roadmap. Once everyone\'s in agreement, they\'ll be your go-to person, handling all the details, finding the right talent, and managing communication and revisions. You can count on them to make sure your project wraps up just the way you want it!'
                        : `Take it easy! If you're feeling stuck after starting your project on your own, it might be time to bring in a project manager. They'll kick things off by verifying the roadmap, and once everyone's on the same page, they’ll be your go-to person. They’ll handle all the details, find the right talent, and manage communication and revisions. You can count on them to make sure your project wraps up just the way you want it!`}</p>
                      {project.status === EProjectStatus.IN_PROGRESS && <p className='mt-2 italic'>Since your project is already in progress, we'll set it back to draft mode so you and your PM can make changes together.</p>}
                    </div>
                  </div>
                  <div className='mt-4 flex flex-row gap-4 justify-end'>
                    {canStartProjectSelfManaged && <OutlinedButton
                      theme='primary'
                      icon={<IoConstructOutline />}
                      iconPosition='leading'
                      onClick={() => {
                        setShowStartProjectSelfManagedLightbox(true);
                      }}>Do It Yourself</OutlinedButton>}
                    {canRequestRoadmapVerification && <FilledButton
                      theme='primary'
                      icon={<IoShieldHalfOutline />}
                      iconPosition='leading'
                      onClick={() => {
                        setShowRequestRoadmapVerificationLightbox(true);
                      }}>Expert Review & PM Support</FilledButton>}
                    {canStartProjectWithPm && <FilledButton
                      theme='primary'
                      icon={<IoShieldHalfOutline />}
                      iconPosition='leading'
                      onClick={() => {
                        setShowStartProjectWithPmLightbox(true);
                      }}>Proceed with PM Support</FilledButton>}
                    {canHirePmForVerifiedProject && <FilledButton
                      theme='primary'
                      icon={<IoShieldHalfOutline />}
                      iconPosition='leading'
                      onClick={() => {
                        setShowHirePmForVerifiedProjectLightbox(true);
                      }}>Hire Project Manager</FilledButton>}
                  </div>
                </div>}

              {/* Info Box for status explanation (Employer) */}
              {isEmployer && project.status === EProjectStatus.DRAFT && tasks.length === 0 ? <InfoBox title='What now?'>
                You’ve successfully created your project! Once you’re happy with all the <a onClick={() => goToProjectDetails(project._id!, EProjectDetailsNav.OVERVIEW)}>details</a>, go ahead and start putting together the project <a onClick={() => goToProjectDetails(project._id!, EProjectDetailsNav.ROADMAP)}>roadmap</a>. Let’s get this show on the road!              </InfoBox>
                : isEmployer && project.status === EProjectStatus.DRAFT && project._waitingForRoadmapVerification && !project.currentRoadmapVerificationPm
                  ? <InfoBox title='What now?'>
                    Sit back and relax! We're currently on the lookout for the perfect person to solidify your <a onClick={() => goToProjectDetails(project._id!, EProjectDetailsNav.OVERVIEW)}>project details</a> and <a onClick={() => goToProjectDetails(project._id!, EProjectDetailsNav.ROADMAP)}>roadmap</a> and manage your project.
                  </InfoBox>
                  : isEmployer && project.status === EProjectStatus.DRAFT && project._waitingForRoadmapVerification && project.currentRoadmapVerificationPm
                    ? <InfoBox title='What now?'>
                      A human expert has been assigned to your project! Feel free to chat and share any information you think will help your PM have everything they need to make your project a success. Let’s make it happen together!
                    </InfoBox>
                    : isEmployer && (project.status || 0) >= EProjectStatus.IN_PROGRESS && (project.status || Infinity) < EProjectStatus.COMPLETED && project.ownerManaged === false
                      ? <InfoBox title='What now?'>
                        Great news—your project is now underway! To let the PM assign <a onClick={() => goToProjectDetails(project._id!, EProjectDetailsNav.TEAM)}>talent</a> to your tasks, <a onClick={() => goToProjectDetails(project._id!, EProjectDetailsNav.ROADMAP)}>please make the payment first</a>. You can pay for each task one at a time, or if you want several tasks to be processed simultaneously, feel free to pay for multiple tasks at once. Let’s keep the momentum going!
                      </InfoBox>
                      : isEmployer && (project.status || 0) >= EProjectStatus.IN_PROGRESS && (project.status || Infinity) < EProjectStatus.COMPLETED && project.ownerManaged === true
                        ? <InfoBox title='What now?'>
                          Awesome news—your project is in progress now! To assign talent to your tasks, please make the payment first. And if you’re feeling stuck, remember you can still hire a PM to help out. To handle task applications or invitations, see the <a onClick={() => goToProjectDetails(project._id!, EProjectDetailsNav.TEAM)}>Teams</a> section.
                          To find more talent, feel free to <a onClick={() => goToBrowseFreelancers()}>browse talent</a>. Let’s keep things moving!
                        </InfoBox>
                        : null}


              <div className='my-8'>
                <ProjectDetailsMetrics
                  project={project}
                  tasks={tasks}
                />
              </div>

              {/* <div className="my-8">
                {canRequestRoadmapVerification && <OfferHumanRoadmapVerificationToClient
                  project={project}
                  setProject={setProject}
                  onUpdateTasks={setTasks}
                />}
              </div> */}

              <div className=" mx-auto flex max-w-7xl items-start gap-x-8">
                <aside className="sticky top-8 hidden w-44 shrink-0 lg:block">
                  <ProjectDetailsNavigation
                    project={project}
                    tasks={tasks}
                    projectManager={projectManager || null}
                    team={team}
                    employer={employer || null}
                    isPm={isPm}
                    isEmployer={isEmployer}
                    isFreelancer={isFreelancer}
                    isOutsideFreelancer={isOutsideFreelancer}
                    isOutsideEmployer={isOutsideEmployer}
                    setProject={setProject}
                    setTasks={setTasks}
                    navigation={navigation}
                    setNavigation={setNavigation}
                  />
                </aside>


                <main className="flex-1">
                  {navigation.find(nav => nav.current && nav.nav === EProjectDetailsNav.OVERVIEW) &&
                    <ProjectDetailsOverview
                      isPm={isPm}
                      isEmployer={isEmployer}
                      isRoadmapVerificationPm={isRoadmapVerificationPm}
                      isFreelancer={isFreelancer}
                      isOutsider={isOutsideFreelancer || isOutsideEmployer}
                      project={project}
                      setProject={setProject} />}

                  {navigation.find(nav => nav.current && nav.nav === EProjectDetailsNav.ROADMAP) &&
                    <ProjectDetailsRoadmap
                      project={project}
                      tasks={tasks}
                      projectManager={projectManager || null}
                      team={team}
                      isPm={isPm}
                      isRoadmapVerificationPm={isRoadmapVerificationPm}
                      isEmployer={isEmployer}
                      isFreelancer={isFreelancer}
                      isOutsideFreelancer={isOutsideFreelancer}
                      isOutsideEmployer={isOutsideEmployer}

                      setProject={setProject}
                      setTasks={setTasks}
                      onAddTask={onAddTask}
                      onUpdateTask={onUpdateTask}
                      onUpdateProject={setProject}
                    />}

                  {navigation.find(nav => nav.current && nav.nav === EProjectDetailsNav.TEAM) &&
                    <ProjectDetailsTeam
                      project={project}
                      tasks={tasks}
                      roadmapVerificationPm={roadmapVerificationPm || null}
                      projectManager={projectManager || null}
                      team={team}
                      employer={employer || null}
                      applications={applications}
                      invitations={invitations}
                      applicants={applicants}
                      invitedFreelancers={invitedFreelancers}
                      isPm={isPm}
                      isRoadmapVerificationPm={isRoadmapVerificationPm}
                      isEmployer={isEmployer}
                      isFreelancer={isFreelancer}
                      isOutsideFreelancer={isOutsideFreelancer}
                      isOutsideEmployer={isOutsideEmployer}
                      setProject={setProject}
                      setTasks={setTasks}
                      setTeam={setTeam}
                      onUpdateApplication={onUpdateApplication}
                      onUpdateInvitation={onUpdateInvitation}
                    />}

                  {navigation.find(nav => nav.current && nav.nav === EProjectDetailsNav.ATTACHMENTS) &&
                    <ProjectDetailsAttachments
                      isPm={isPm}
                      isEmployer={isEmployer}
                      isFreelancer={isFreelancer}
                      isOutsider={isOutsideFreelancer || isOutsideEmployer}
                      project={project}
                      setProject={setProject}
                    />}

                  {navigation.find(nav => nav.current && nav.nav === EProjectDetailsNav.CHAT) &&
                    <ProjectDetailsChat
                      isPm={isPm}
                      isEmployer={isEmployer}
                      isFreelancer={isFreelancer}
                      isOutsider={isOutsideFreelancer || isOutsideEmployer}
                      project={project}
                      setProject={setProject}
                    />}

                </main>

              </div>
            </div>
      }

      {project && showRequestRoadmapVerificationLightbox && <RequestRoadmapVerificationLightbox
        onClose={() => { setShowRequestRoadmapVerificationLightbox(false) }}
        projectId={project._id!}
        onUpdateProject={setProject}
        onUpdateTasks={setTasks}
      />}

      {project && showStartProjectSelfManagedLightbox && <StartProjectSelfManagedLightbox
        project={project}
        onClose={() => setShowStartProjectSelfManagedLightbox(false)}
        onProjectUpdate={setProject}
        onTasksUpdate={setTasks}
      />}

      {project && showStartProjectWithPmLightbox && <StartProjectWithPmLightbox
        projectId={project._id!}
        onClose={() => setShowStartProjectWithPmLightbox(false)}
        onProjectUpdate={setProject}
        onTasksUpdate={setTasks}
      />}

      {project && showHirePmForVerifiedProjectLightbox && <HirePmForVerifiedProject
        projectId={project._id!}
        onClose={() => setShowHirePmForVerifiedProjectLightbox(false)}
        onProjectUpdate={setProject}
        onTasksUpdate={setTasks}
      />}


    </div >
  );
};

export default ProjectDetailsPage;